import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { PropsWithChildren, ReactNode, RefObject, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Modal, Select } from '@/components/ui/daisy';
import useDeferredUnmount from '@/data/hook/shared/useDeferredUnmount';

const EXIT_ANIMATION_MS = 300;

type BottomDrawerProps = PropsWithChildren<{
  dialogRef: RefObject<HTMLDialogElement>;
  isOpen: boolean;
  onClose(): void;
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}>;

type BottomDrawerTriggerProps = {
  label: string;
  isOpen: boolean;
  onClick(): void;
  icon?: ReactNode;
  className?: string;
  'aria-label'?: string;
  testId?: string;
};

function BottomDrawerRoot({
  dialogRef,
  isOpen,
  onClose,
  title,
  description,
  children,
  className,
  showCloseButton = true,
}: BottomDrawerProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });
  const titleId = useId();
  const descriptionId = useId();
  const shouldRenderContent = useDeferredUnmount(isOpen, EXIT_ANIMATION_MS);

  return (
    <Modal
      ref={dialogRef}
      position="bottom"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
    >
      <Modal.Box
        className={twMerge(
          'flex flex-col gap-4 rounded-t-3xl bg-base-200',
          className,
        )}
      >
        <ConditionalRenderer condition={showCloseButton}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t('close')}
            shape="wide"
            onClick={onClose}
            className="self-center text-base-content"
          >
            <ChevronDownIcon className="size-6" />
          </Button>
        </ConditionalRenderer>
        <ConditionalRenderer condition={title || description}>
          <div className="flex flex-col gap-2">
            <ConditionalRenderer condition={title}>
              <h2 id={titleId} className="font-semibold">
                {title}
              </h2>
            </ConditionalRenderer>
            <ConditionalRenderer condition={description}>
              <p id={descriptionId} className="text-sm">
                {description}
              </p>
            </ConditionalRenderer>
          </div>
        </ConditionalRenderer>
        <ConditionalRenderer condition={shouldRenderContent}>
          {children}
        </ConditionalRenderer>
      </Modal.Box>
      <Modal.Backdrop />
    </Modal>
  );
}

function BottomDrawerTrigger({
  label,
  isOpen,
  onClick,
  icon,
  className,
  'aria-label': ariaLabel,
  testId,
}: BottomDrawerTriggerProps) {
  return (
    <Select.Wrapper className={twMerge('w-full bg-base-100', className)}>
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        data-testid={testId}
        className="flex min-w-0 grow cursor-pointer items-center gap-2 text-left"
      >
        {icon}
        <span className="truncate">{label}</span>
      </button>
    </Select.Wrapper>
  );
}

const BottomDrawer = Object.assign(BottomDrawerRoot, {
  Trigger: BottomDrawerTrigger,
});

export default BottomDrawer;
